import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import TypewriterLabel from './TypewriterLabel';
import PixelArtLogo from './PixelArtLogo';

const TOOL_LINKS = [
  { to: '/options-breakdown', label: 'Option Analysis' },
  { to: '/iv-calculator',     label: 'IV Calculator' },
  { to: '/scrip-analysis',    label: 'Scrip Analysis' },
];

const NAV_LINKS = [
  { to: '/blog',  label: 'Blog' },
  { to: '/faq',   label: 'FAQ' },
  { to: '/about', label: 'About' },
];

function SunIcon() {
  return (
    <svg className="w-5 h-5 ov-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2"  x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2"  y1="12" x2="5"  y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <line x1="4.22"  y1="4.22"  x2="6.34"  y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.78" y2="19.78" />
      <line x1="19.78" y1="4.22"  x2="17.66" y2="6.34" />
      <line x1="6.34"  y1="17.66" x2="4.22"  y2="19.78" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="w-5 h-5 ov-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { dark, toggle } = useDarkMode();

  const navClass = (isActive) =>
    `px-3 py-1.5 rounded-clay-sm text-sm font-medium transition-all duration-150 ${
      isActive ? 'nav-link-active' : 'nav-link'
    }`;

  const mobileNavClass = (isActive) =>
    `px-3 py-2 rounded-clay-sm text-sm font-medium transition-all duration-150 ${
      isActive ? 'nav-link-active' : 'nav-link'
    }`;

  return (
    <header className="sticky top-0 z-30 px-4 sm:px-6 pt-3 pointer-events-none">
      <div className="max-w-7xl mx-auto">
        <div className="nav-bar backdrop-blur-md rounded-2xl shadow-clay dark:shadow-clay-dark transition-colors duration-200 pointer-events-auto">
          <div className="px-4">
            <div className="flex items-center justify-between h-14">

              {/* Brand */}
              <Link to="/" className="flex items-center gap-3">
                <PixelArtLogo />
                <span className="flex items-end gap-1">
                  <span className="text-xl font-bold nav-brand tracking-tight ov-logo">OptionView</span>
                  <span className="text-xs text-neutral-400 dark:text-neutral-500 leading-none mb-1.5">(alpha)</span>
                </span>
              </Link>

              {/* Desktop nav */}
              <nav className="hidden md:flex items-center gap-1">
                {TOOL_LINKS.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) => navClass(isActive)}
                  >
                    <TypewriterLabel>{label}</TypewriterLabel>
                  </NavLink>
                ))}

                {NAV_LINKS.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) => navClass(isActive)}
                  >
                    <TypewriterLabel>{label}</TypewriterLabel>
                  </NavLink>
                ))}

                {/* Dark mode toggle */}
                <button
                  onClick={toggle}
                  className="ml-2 p-2 rounded-clay-sm nav-icon-btn shadow-clay-btn dark:shadow-clay-btn-dark transition-all duration-150"
                  aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {dark ? <SunIcon /> : <MoonIcon />}
                </button>
              </nav>

              {/* Mobile: dark mode toggle + hamburger */}
              <div className="md:hidden flex items-center gap-1">
                <button
                  onClick={toggle}
                  className="p-2 rounded-clay-sm nav-icon-btn shadow-clay-btn dark:shadow-clay-btn-dark transition-all duration-150"
                  aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {dark ? <SunIcon /> : <MoonIcon />}
                </button>
                <button
                  className="p-2 rounded-clay-sm nav-icon-btn shadow-clay-btn dark:shadow-clay-btn-dark transition-all duration-150"
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-label="Toggle menu"
                >
                  {menuOpen ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>

            </div>
          </div>

          {/* Mobile dropdown menu — inside the floating card */}
          {menuOpen && (
            <nav className="md:hidden nav-mobile-menu">
              <div className="px-4 py-2 flex flex-col gap-1">
                {[...TOOL_LINKS, ...NAV_LINKS].map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) => mobileNavClass(isActive)}
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            </nav>
          )}

        </div>
      </div>
    </header>
  );
}
